import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";
import { checkRateLimit } from "@/lib/rateLimiting";

interface ReportContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId?: string;
  commentId?: string;
  contentType: "post" | "comment";
}

type ReportReason = "spam" | "harassment" | "misinformation" | "inappropriate" | "other";

const reportReasons: { value: ReportReason; label: string; description: string }[] = [
  { value: "spam", label: "Spam", description: "Repeated or promotional content" },
  { value: "harassment", label: "Harassment", description: "Bullying or threatening behavior" },
  { value: "misinformation", label: "Misinformation", description: "False or misleading health information" },
  { value: "inappropriate", label: "Inappropriate", description: "Offensive or explicit content" },
  { value: "other", label: "Other", description: "Something else that violates guidelines" },
];

export function ReportContentDialog({
  open,
  onOpenChange,
  postId,
  commentId,
  contentType,
}: ReportContentDialogProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState<ReportReason>("spam");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason) {
      toast({
        title: "Select a reason",
        description: "Please select why you're reporting this content",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to report content",
          variant: "destructive",
        });
        return;
      }

      // Check rate limit
      const rateLimitResult = await checkRateLimit(user.id, 'content_report');
      if (!rateLimitResult.allowed) {
        toast({
          title: "Too many reports",
          description: rateLimitResult.error,
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }

      const { error } = await supabase.from("content_reports").insert({
        reporter_user_id: user.id,
        reported_post_id: postId || null,
        reported_comment_id: commentId || null,
        reason,
        details: details.trim() || null,
      });

      if (error) {
        if (error.code === "23505") {
          // Unique constraint violation
          toast({
            title: "Already reported",
            description: "You've already reported this content",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: "Report submitted",
        description: "Thank you. Our moderation team will review this content.",
      });

      setReason("spam");
      setDetails("");
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error submitting report",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Report {contentType === "post" ? "Post" : "Comment"}
          </DialogTitle>
          <DialogDescription>
            Help us maintain a safe and supportive community by reporting inappropriate content.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3">
            <Label>Why are you reporting this?</Label>
            <RadioGroup value={reason} onValueChange={(val) => setReason(val as ReportReason)}>
              {reportReasons.map((option) => (
                <div key={option.value} className="flex items-start space-x-3 space-y-0">
                  <RadioGroupItem value={option.value} id={option.value} />
                  <div className="flex-1">
                    <Label htmlFor={option.value} className="font-normal cursor-pointer">
                      {option.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Additional details (optional)</Label>
            <Textarea
              id="details"
              placeholder="Provide any additional context that might help our review..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {details.length}/500 characters
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
